import { Stack } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React from 'react'
import {
  EVENT,
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN,
} from '../../../../constants'
import { isGiftCard } from '../../../../functions/lineItem'
import { isNonFulfillmentOnAccountSale } from '../../../../functions/sale'
import {
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../models/fulfilment'
import { LineItem } from '../../../../models/line-item'
import {
  useSaleInventoryData,
  useVdFeatureManager,
} from '../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { selectSaleReturnFor } from '../../../../state/selectors'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  openFulfilmentLineItemPopover,
  setLineItemFulfilmentNoteError,
} from '../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { FulfilmentSelect } from '../../../react/modals/mark-as-unfulfilled-modal/Steps/Step1/FulfillmentSelect'
import { NoteError } from '../../../shared/NoteInput'
import { LineItemFulfilmentAddress } from './LineItemFulfilmentAddress'
import { LineItemFulfilmentNote } from './LineItemFulfilmentNote'
import { useAutoPopulateFulfillmentAddresses } from './hook/useAutoPopulateFulfillmentAddresses'
import { useSaleFulfilment } from './hook/useSaleFulfilment'

export const LineItemDisplayFulfilment: React.FC<{
  lineItem: LineItem
  onSelectFulfilmentType?: (type: FulfilmentType) => void
}> = ({ lineItem: defaultLineItem, onSelectFulfilmentType }) => {
  const t = useT()

  const currentSale = useAppSelector(selectCurrentSale)
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)
  const saleInventoryData = useSaleInventoryData()
  const feature = useVdFeatureManager()
  const dispatch = useAppDispatch()

  const {
    currentLineItem,
    saleId,
    hasFulfilment,
    fulfilmentDetail,
    setFulfilmentType,
    setFulfilmentNote,
  } = useSaleFulfilment(defaultLineItem.localId)

  // Auto populate fulfillment addresses based on customer addresses
  useAutoPopulateFulfillmentAddresses(fulfilmentDetail)

  const hasId = !!saleId
  const isParkedSale = hasId && !currentSale.isSavedUnfulfilled

  const isDisabled =
    currentLineItem?.confirmed ||
    currentLineItem?.isReturn ||
    Number(currentLineItem?.quantity) < 0 ||
    (hasId && hasFulfilment) ||
    isReferencedReturn ||
    isNonFulfillmentOnAccountSale(currentSale) ||
    isGiftCard(currentLineItem?.product)

  const handleNoteErrorsChange = (errors: NoteError[]) => {
    if (errors.length === 0) {
      setLineItemFulfilmentNoteError({
        id: currentLineItem.localId,
        error: null,
      })

      return
    }

    setLineItemFulfilmentNoteError({
      id: currentLineItem.localId,
      error: errors[0],
    })
  }

  const shouldShowFulfilmentAddress =
    getFeatureFromCache(FEATURE_CAPTURE_ADDRESS_SELL_SCREEN) &&
    currentLineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
    !isParkedSale

  return (
    <Stack space={4}>
      <FulfilmentSelect
        showUnspecified
        disabled={isDisabled}
        onTypeChange={type => {
          setFulfilmentType(type, fulfilmentDetail?.note || '')
          if (onSelectFulfilmentType) {
            onSelectFulfilmentType(type)
          }
          if (
            feature.get('fulfillment_lif_b3') &&
            !isDefinedFulfilmentType(type)
          ) {
            saleInventoryData.enableInventoryValidation(currentLineItem)
            if (
              !isParkedSale &&
              Number(currentLineItem.quantity) > 0 &&
              !saleInventoryData.hasInventory(currentLineItem)
            ) {
              dispatch(openFulfilmentLineItemPopover(currentLineItem.localId))
            }
          } else {
            saleInventoryData.disableInventoryValidation(currentLineItem)
          }
          saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
        }}
        defaultType={currentLineItem?.fulfilmentType}
        stretched
        message={t(
          'You can select one method per sale. To set another method, create another sale.',
          {
            _context: 'Note explaining how fulfillment methods work',
          }
        )}
      />

      {shouldShowFulfilmentAddress && (
        <LineItemFulfilmentAddress
          fulfillmentDetail={fulfilmentDetail}
          lineItemLocalId={defaultLineItem.localId}
          isModalOpeningOnError={fulfilmentDetail?.shouldAddNewAddress}
        />
      )}

      {!!currentLineItem?.fulfilmentType &&
        currentLineItem.fulfilmentType !== FulfilmentType.UNSPECIFIED && (
          <LineItemFulfilmentNote
            note={fulfilmentDetail?.note || ''}
            isDisabled={isDisabled}
            onChange={setFulfilmentNote}
            onErrorsChange={handleNoteErrorsChange}
          />
        )}
    </Stack>
  )
}
